import { BACKEND_API_BASE_URL } from '@/services/constants';
import { getClaimNumber } from '@/utils/functions';
import { useCallback, useEffect, useRef } from 'react';
import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';
import {
  generateUUID,
  getIPAsync,
  getStoredId,
  getUTMTags,
  patchHistoryListeners,
  setStoredId,
} from './utils';
import { setJourneyVersion, type JOURNEY_VERSION } from '@/routes/journey.routes';

export default function UserVisitTracker() {
  const isTrackingRef = useRef(false);
  const lastTrackedUrlRef = useRef<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const sendWebVitalsReport = useCallback(
    (distinctId: string, initialPathname: string, initialUrl: string) => (metric: any) => {
      const payload = {
        distinctId,
        pathname: initialPathname,
        url: initialUrl,
        metric: metric?.name,
        score: metric?.value,
        rating: metric?.rating,
        claimNumber: getClaimNumber(),
      };

      console.log('[WebVitals]', metric?.name, {
        score: metric?.value,
        rating: metric?.rating,
        pathname: initialPathname,
        payload,
      });

      if (window.location.hostname.includes('localhost')) {
        console.log('[WebVitals] Skipping DB report on localhost');
        return;
      }

      fetch(BACKEND_API_BASE_URL + '/session-sync/update/webvitals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch((err) => {
        console.error('[WebVitals] Failed to send report', err);
      });
    },
    []
  );

  const track = useCallback(async () => {
    if (window.location.hostname.includes('localhost')) {
      return;
    }

    const currentUrl = window.location.href;
    if (lastTrackedUrlRef.current === currentUrl) {
      return;
    }

    if (isTrackingRef.current) return;
    isTrackingRef.current = true;
    lastTrackedUrlRef.current = currentUrl;

    let distinctId = getStoredId();
    if (!distinctId) {
      distinctId = generateUUID();
      setStoredId(distinctId);
    }

    try {
      const ip = await getIPAsync();

      const payload: any = {
        distinctId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        language: navigator.language,
        screen: {
          width: window.screen.width,
          height: window.screen.height,
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer,
        pathname: window.location?.pathname,
        url: window.location.href,
        utm: getUTMTags(),
        ip: ip || '',
        claimNumber: getClaimNumber(),
      };

      await fetch(BACKEND_API_BASE_URL + '/session-sync/record', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch (err) {
      console.error('Visit tracking failed', err);
    } finally {
      isTrackingRef.current = false;
    }
  }, []);

  const autoUpdateJourneyVersion = () => {
    const url = window.location.href;
    const versionRegex = /[/-](v\d+)/;
    const match = url.match(versionRegex);
    if (match) {
      const version = match[0].replace(/[-/]/, '');
      setJourneyVersion(version as JOURNEY_VERSION);
    }
  };

  const perform = useCallback(
    (immediate = false) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (immediate) {
        track();
        autoUpdateJourneyVersion();
      } else {
        timeoutRef.current = setTimeout(() => {
          track();
          autoUpdateJourneyVersion();
        }, 100);
      }
    },
    [track]
  );

  useEffect(() => {
    let distinctId = getStoredId();
    if (!distinctId) {
      distinctId = generateUUID();
      setStoredId(distinctId);
    }

    const initialPathname = window.location.pathname;
    const initialUrl = window.location.href;

    const report = sendWebVitalsReport(distinctId, initialPathname, initialUrl);

    onCLS(report);
    onINP(report);
    onLCP(report);
    onFCP(report);
    onTTFB(report);
  }, [sendWebVitalsReport]);

  useEffect(() => {
    // Initial mount: track instantly so we don't miss the landing page visit
    perform(true);

    // Track subsequent route changes after a brief delay
    const cleanup = patchHistoryListeners(() => {
      perform(false);
    });

    return () => {
      cleanup();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [perform]);

  return null;
}
